import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast, Toaster } from "sonner";
import { ShieldCheck, UserPlus, Trash2, ArrowLeft, Loader2, Crown, Map as MapIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import {
  adminListEmails, adminAddEmail, adminRemoveEmail,
  adminGetMapConfig, adminUpdateMapConfig, POLY_LAYER_LABELS,
} from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function Admin() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [emails, setEmails] = useState([]);
  const [newEmail, setNewEmail] = useState("");
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [mapCfg, setMapCfg] = useState(null);

  const load = async () => {
    try {
      const [em, cfg] = await Promise.all([adminListEmails(), adminGetMapConfig()]);
      setEmails(em);
      setMapCfg(cfg);
    } catch {
      toast.error("No se pudo cargar la información");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const toggleMapLayer = async (key) => {
    if (!mapCfg) return;
    const next = { ...mapCfg, [key]: !mapCfg[key] };
    setMapCfg(next);
    try {
      const saved = await adminUpdateMapConfig({ [key]: next[key] });
      setMapCfg(saved);
      toast.success(`Capa ${POLY_LAYER_LABELS[key]} ${saved[key] ? "activada" : "desactivada"}`);
    } catch {
      setMapCfg(mapCfg);
      toast.error("No se pudo actualizar la configuración");
    }
  };

  const add = async (e) => {
    e.preventDefault();
    const email = newEmail.trim().toLowerCase();
    if (!email || !email.includes("@")) { toast.warning("Ingresa un correo válido"); return; }
    setAdding(true);
    try {
      await adminAddEmail(email);
      setNewEmail("");
      await load();
      toast.success(`Autorizado: ${email}`);
    } catch {
      toast.error("No se pudo agregar el correo");
    } finally {
      setAdding(false);
    }
  };

  const remove = async (email) => {
    try {
      await adminRemoveEmail(email);
      await load();
      toast.success(`Acceso revocado: ${email}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo eliminar");
    }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-page">
      <Toaster position="top-right" richColors />
      <header className="bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-guinda flex items-center justify-center">
              <ShieldCheck className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="font-heading text-lg font-bold text-slate-900 leading-tight">Administración de Accesos</h1>
              <p className="text-xs text-slate-500">Correos autorizados para usar la plataforma</p>
            </div>
          </div>
          <Button variant="outline" onClick={() => navigate("/dashboard")} data-testid="back-dashboard-btn"
            className="h-9 border-slate-200 text-slate-600">
            <ArrowLeft className="w-4 h-4 mr-1.5" /> Volver
          </Button>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-6 space-y-5">
        <form onSubmit={add} className="bg-white rounded-xl border border-slate-200 shadow-sm p-4 flex flex-col sm:flex-row gap-3 sm:items-end">
          <div className="flex-1">
            <label className="text-xs font-semibold text-slate-600">Autorizar nuevo correo de Google</label>
            <Input data-testid="new-email-input" value={newEmail} onChange={(e) => setNewEmail(e.target.value)}
              placeholder="usuario@dominio.com" type="email" className="mt-1 h-10" />
          </div>
          <Button type="submit" disabled={adding} data-testid="add-email-btn"
            className="h-10 bg-guinda hover:bg-guinda-dark text-white">
            {adding ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <UserPlus className="w-4 h-4 mr-1.5" />}
            Autorizar
          </Button>
        </form>

        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
            <h2 className="font-heading text-sm font-bold text-slate-800">Correos autorizados</h2>
            <span className="text-xs text-slate-400">{emails.length} en total</span>
          </div>
          {loading ? (
            <div className="p-8 flex justify-center"><Loader2 className="w-6 h-6 text-guinda animate-spin" /></div>
          ) : (
            <ul className="divide-y divide-slate-100" data-testid="email-list">
              {emails.map((e) => (
                <li key={e.email} className="px-4 py-3 flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2 min-w-0">
                    {e.role === "admin" && <Crown className="w-4 h-4 text-amber-500 shrink-0" />}
                    <span className="text-sm text-slate-700 truncate">{e.email}</span>
                    {e.role === "admin" && (
                      <span className="text-[10px] font-semibold text-amber-600 bg-amber-50 rounded-full px-2 py-0.5">ADMIN</span>
                    )}
                  </div>
                  {e.email !== user?.email && e.role !== "admin" ? (
                    <Button variant="ghost" size="sm" onClick={() => remove(e.email)}
                      data-testid={`remove-${e.email}`}
                      className="text-slate-400 hover:text-red-600 hover:bg-red-50 h-8">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  ) : (
                    <span className="text-[11px] text-slate-300">—</span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden" data-testid="map-config-card">
          <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2">
            <MapIcon className="w-4 h-4 text-guinda" />
            <div>
              <h2 className="font-heading text-sm font-bold text-slate-800">Configuración del mapa</h2>
              <p className="text-[11px] text-slate-400">Controla qué capas de polígonos pueden ver los usuarios</p>
            </div>
          </div>
          {loading || !mapCfg ? (
            <div className="p-8 flex justify-center"><Loader2 className="w-6 h-6 text-guinda animate-spin" /></div>
          ) : (
            <ul className="divide-y divide-slate-100">
              {["secciones", "areas", "colonias"].map((k) => (
                <li key={k} className="px-4 py-3 flex items-center justify-between gap-3">
                  <span className="text-sm text-slate-700">{POLY_LAYER_LABELS[k]}</span>
                  <Switch data-testid={`map-layer-${k}`} checked={!!mapCfg[k]}
                    onCheckedChange={() => toggleMapLayer(k)} />
                </li>
              ))}
            </ul>
          )}
        </div>
      </main>
    </div>
  );
}
